import pencilIcon from "@assets/images/pencil-alt.svg";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { CSSProperties } from "@material-ui/styles";
import MyTypography from "@saleor/components/MyTypography";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import { GetListUserManagementQuery } from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { renderCollection } from "@saleor/misc";
import { ListProps, RelayToFlat, SortPage } from "@saleor/types";
import { UserListSortField } from "@saleor/userManagement/urls";
import { getArrowDirection } from "@saleor/utils/sort";
import moment from "moment-timezone/moment-timezone-utils";
import React, { useRef } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

import UISwitch from "../UISwitch";

export interface UserListTableProps
  extends ListProps,
    SortPage<UserListSortField> {
  data: RelayToFlat<GetListUserManagementQuery["scgpUsers"]>;
  loading?: boolean;
  totalCount: number | null;
  latestPageItemNumber: number | null;
  group?: number | string;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
  onViewUserDetail: (id, params) => void;
  onEditUser?: (event) => void;
}

const freezeCell: CSSProperties = {
  position: "sticky",
  right: 0,
  minWidth: 48,
  padding: 0,
  borderRadius: "0px 2px 2px 0px",
  background: "inherit",
  textAlign: "center"
};

const useStyles = makeStyles(
  theme => ({
    titleTable: {
      marginTop: "40px"
    },
    root: {
      marginTop: "24px",
      overflowX: "auto",
      width: "100%",
      border: `1px solid ${theme.palette.grey[300]}`,
      borderBottom: 0,
      borderRadius: "2px",
      "& .MuiTableCell-head": {
        "& *": {
          // color: theme.palette.grey[600]
        }
      },
      "&::-webkit-scrollbar": {
        height: "14px",
        background: "#FFFFFF"
      },
      "&::-webkit-scrollbar-track": {
        borderRadius: 10
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#CCCCCC",
        borderRadius: 10,
        border: "4px solid #FFFFFF",
        height: "6px"
      },
      "& .MuiTableCell-root": {
        height: "75px"
      },
      "& .MuiTableCell-body": {
        color: "#222222"
      }
    },
    checked: {},
    focusVisible: {},
    cursorPointer: {
      cursor: "pointer"
    },
    link: {
      cursor: "pointer"
    },
    colPiNumber: {
      minWidth: "200px",
      "& .MuiTypography-root": {
        color: theme.palette.info.main
      }
    },
    col120: {
      minWidth: 120,
      color: theme.palette.grey[900]
    },
    col150: {
      minWidth: 150,
      color: theme.palette.grey[900]
    },
    col260: {
      minWidth: 260,
      color: theme.palette.grey[900]
    },
    pagination: {
      background: "rgba(212, 244, 248, 0.8)"
    },
    paginateFooter: {
      background: "rgba(212, 244, 248, 0.8)",
      padding: "12px 20px",
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      borderRight: `1px solid ${theme.palette.grey[300]}`,
      borderLeft: `1px solid ${theme.palette.grey[300]}`
    },
    rowClickable: {
      cursor: "pointer"
    },
    colActive: {
      ...freezeCell,
      right: 48,
      boxShadow: "-4px 0px 6px rgb(0 0 0 / 4%)",
      paddingLeft: "8px",
      minWidth: 96,
      "& .MuiFormControlLabel-root": {
        margin: 0
      }
    },
    colFreeze: {
      ...freezeCell
    },
    statusIcon: {
      width: 8,
      height: 8,
      marginRight: 8,
      borderRadius: "50%"
    },
    title: {
      marginTop: 40,
      marginBottom: 24
    },
    flex: { display: "flex", alignItems: "center" },
    thumb: {
      width: 10,
      height: 10
    },
    track: {
      borderRadius: 26 / 2,
      border: `1.5px solid ${theme.palette.grey[300]}`,
      backgroundColor: theme.palette.grey[200],
      opacity: 1,
      transition: theme.transitions.create(["background-color", "border"])
    },
    switchBase: {
      padding: 1,
      top: 0,
      margin: 0,
      left: -4,
      "&$checked": {
        transform: "translateX(16px)",
        color: theme.palette.common.white,
        "& + $track": {
          backgroundColor: `${theme.palette.info.main} !important`,
          opacity: 1,
          border: `1.5px solid ${theme.palette.info.dark}`
        }
      },
      "&$focusVisible $thumb": {
        color: "#52d869",
        border: "6px solid #fff"
      }
    },
    rootSwitch: {
      height: 18,
      width: 26,
      borderRadius: 24,
      padding: 0
    }
  }),
  { name: "UserListTable" }
);

const UserListTable: React.FC<UserListTableProps> = props => {
  const {
    data,
    disabled,
    settings,
    pageInfo,
    totalCount,
    latestPageItemNumber,
    group,
    sort,
    onSort,
    onPreviousPage,
    onNextPage,
    onUpdateListSettings,
    onNextLastPage,
    onPreviousFirstPage,
    onEditUser,
    onViewUserDetail
  } = props;
  const intl = useIntl();
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);
  return (
    <>
      <MyTypography variant="h7" className={classes.titleTable}>
        {intl.formatMessage({ defaultMessage: "รายการที่เกี่ยวข้อง" })}
      </MyTypography>
      <div className={classes.root} ref={containerRef}>
        <Table data-testid="table">
          <TableHead>
            <TableRow>
              <TableCellHeader className={classes.colPiNumber}>
                {intl.formatMessage({
                  defaultMessage: "Existing User ID / Exployee ID"
                })}
              </TableCellHeader>
              <TableCellHeader className={classes.col120}>
                {intl.formatMessage({ defaultMessage: "Domain User" })}
              </TableCellHeader>
              <TableCellHeader
                className={classes.col120}
                direction={
                  sort.sort === UserListSortField.user_group
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(UserListSortField.user_group)}
              >
                {intl.formatMessage({ defaultMessage: "User Group" })}
              </TableCellHeader>
              <TableCellHeader className={classes.col260}>
                {intl.formatMessage({ defaultMessage: "Role" })}
              </TableCellHeader>
              <TableCellHeader
                className={classes.col260}
                direction={
                  sort.sort === UserListSortField.bu
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(UserListSortField.bu)}
              >
                {intl.formatMessage({ defaultMessage: "BU" })}
              </TableCellHeader>
              <TableCellHeader
                className={classes.col120}
                direction={
                  sort.sort === UserListSortField.first_name
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(UserListSortField.first_name)}
              >
                {intl.formatMessage({ defaultMessage: "First Name" })}
              </TableCellHeader>
              <TableCellHeader
                className={classes.col120}
                direction={
                  sort.sort === UserListSortField.last_name
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(UserListSortField.last_name)}
              >
                {intl.formatMessage({ defaultMessage: "Last Name" })}
              </TableCellHeader>
              <TableCellHeader className={classes.col120}>
                {intl.formatMessage({ defaultMessage: "Email" })}
              </TableCellHeader>
              <TableCellHeader className={classes.col260}>
                {intl.formatMessage({ defaultMessage: "Sold to" })}
              </TableCellHeader>
              <TableCellHeader className={classes.col260}>
                {intl.formatMessage({ defaultMessage: "Create Date" })}
              </TableCellHeader>
              <TableCellHeader className={classes.col260}>
                {intl.formatMessage({ defaultMessage: "Update By" })}
              </TableCellHeader>
              <TableCellHeader className={classes.colActive} textAlign="center">
                {intl.formatMessage({ defaultMessage: "Status" })}
              </TableCellHeader>
              <TableCellHeader className={classes.colFreeze} textAlign="center">
                {intl.formatMessage({ defaultMessage: "Edit" })}
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderCollection(data, (item, index) => {
              const groupCurrent = item
                ? item?.permissionGroups?.find(item => item?.id === group)
                : null;
              const groupCurrentAll = item
                ? item?.permissionGroups?.map(item => item.name)
                : null;
              return (
                <TableRow className={classes.rowClickable} key={index}>
                  <TableCell className={classes.colPiNumber}>
                    <MyTypography
                      variant="subtitle3"
                      data-testid="cell-employeeId"
                      onClick={() => onViewUserDetail(item.id, {})}
                    >
                      {item ? (
                        item?.extendData?.employeeId || "-"
                      ) : (
                        <Skeleton />
                      )}
                    </MyTypography>
                  </TableCell>
                  <TableCell>
                    {item ? (
                      item?.extendData?.customerType || "-"
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {item ? (
                      item?.extendData?.userParentGroup?.name || "-"
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {item ? (
                      groupCurrent?.name || groupCurrentAll?.join(", ") || "-"
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {item ? (
                      item?.extendData?.scgpBus?.map(i => i.name).join(", ") ||
                      "-"
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>{item ? item.firstName : <Skeleton />}</TableCell>
                  <TableCell>{item ? item.lastName : <Skeleton />}</TableCell>
                  <TableCell>{item ? item.email : <Skeleton />}</TableCell>
                  <TableCell style={{ whiteSpace: "pre-wrap" }}>
                    {item ? (
                      item?.soldTos
                        ?.map(i => `${i.code} - ${i.name}`)
                        .join(",\n")
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {item ? (
                      moment(item?.dateJoined).format("DD / MM / YYYY")
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {item?.extendData?.updatedBy
                      ? `${item?.extendData?.updatedBy?.firstName} ${item?.extendData?.updatedBy?.lastName}`
                      : "-"}
                  </TableCell>
                  <TableCell
                    className={classes.colActive}
                    data-testid="editUser"
                  >
                    <UISwitch
                      onChange={event => onEditUser({ ...event, id: item.id })}
                      label="Active"
                      name="active"
                      defaultChecked={item?.isActive}
                      id="active"
                    />
                  </TableCell>
                  <TableCell className={classes.colFreeze} id="editUser">
                    <SVG
                      data-testid="editUser-button"
                      src={pencilIcon}
                      onClick={() => onViewUserDetail(item.id, { edit: true })}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {data?.length ? (
          <StickyHorizontalScrollBar container={containerRef} />
        ) : null}
      </div>
      <Box className={classes.paginateFooter} data-testid="pagination-wrapper">
        <TablePaginationCustom
          settings={settings}
          hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
          onNextPage={onNextPage}
          onPreviousFirstPage={onPreviousFirstPage}
          onNextLastPage={onNextLastPage}
          onUpdateListSettings={onUpdateListSettings}
          hasPreviousPage={
            pageInfo && !disabled ? pageInfo.hasPreviousPage : false
          }
          onPreviousPage={onPreviousPage}
          latestPageItemNumber={latestPageItemNumber}
          totalCount={totalCount}
          isBackFistPage
          isNextLastPage
          isShowIndex
          disabled={disabled}
        />
      </Box>
    </>
  );
};

export default UserListTable;
